import React from 'react';
import { useTheme } from '@flowcore-water/ui';
import type { TimelineEntry } from '../../utils/timeline';
import type { TimelineStatus } from '../../types';

interface TimelineCardProps {
  entry: TimelineEntry;
}

const STATUS_CONFIG: Record<TimelineStatus, { label: string; emoji: string }> = {
  complete: { label: 'Complete', emoji: '' },
  'on-track': { label: 'On Track', emoji: '' },
  'at-risk': { label: 'At Risk', emoji: '' },
  behind: { label: 'Behind', emoji: '' },
  overdue: { label: 'Overdue', emoji: '' },
  'no-deadline': { label: 'No Deadline', emoji: '' },
};

export const TimelineCard: React.FC<TimelineCardProps> = ({ entry }) => {
  const { t } = useTheme();
  const config = STATUS_CONFIG[entry.status];
  const statusColor = getStatusColor(entry.status, t);
  const progressPct = entry.totalItems > 0 ? (entry.completedItems / entry.totalItems) * 100 : 0;

  const formatDate = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div
      className="rounded-lg border overflow-hidden"
      style={{
        background: t.cardBg,
        borderColor: t.border,
        borderLeftWidth: '4px',
        borderLeftColor: statusColor,
      }}
    >
      <div className="p-4">
        {/* Header row */}
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold" style={{ color: t.textPrimary }}>
              {entry.qualShortName}
            </h3>
            <span
              className="rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap"
              style={{
                background: statusColor + '20',
                color: statusColor,
                border: `1px solid ${statusColor}40`,
              }}
            >
              {config.label}
            </span>
          </div>

          {/* Deadline date */}
          {entry.deadlineDate && (
            <span className="text-sm" style={{ color: t.textSecondary }}>
              Due {formatDate(entry.deadlineDate)}
              {entry.daysRemaining != null && (
                <span
                  className="ml-2 font-medium"
                  style={{
                    color: entry.daysRemaining < 0 ? t.fail : entry.daysRemaining < 7 ? '#f59e0b' : t.textSecondary,
                  }}
                >
                  ({entry.daysRemaining < 0
                    ? `${Math.abs(entry.daysRemaining)}d overdue`
                    : `${entry.daysRemaining}d remaining`})
                </span>
              )}
            </span>
          )}
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs" style={{ color: t.textSecondary }}>
              {entry.completedItems}/{entry.totalItems} signoffs
            </span>
            <span className="text-xs font-medium" style={{ color: t.textPrimary }}>
              {entry.totalItems > 0 ? Math.round(progressPct) : 0}%
            </span>
          </div>
          <div
            className="h-2 rounded-full overflow-hidden"
            style={{ background: t.borderSubtle }}
          >
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${progressPct}%`,
                background: entry.status === 'complete' ? t.success : statusColor,
              }}
            />
          </div>
        </div>

        {/* Metrics row */}
        <div className="flex flex-wrap gap-4 text-sm">
          {/* Pace needed */}
          {entry.requiredPace != null && entry.status !== 'complete' && (
            <MetricChip
              label="Pace needed"
              value={`${entry.requiredPace.toFixed(1)}/day`}
              color={entry.requiredPace > 3 ? t.fail : entry.requiredPace > 1.5 ? '#f59e0b' : t.success}
            />
          )}

          {/* Time spent */}
          {entry.timing.daysSpent != null && (
            <MetricChip
              label="Time at level"
              value={`${entry.timing.daysSpent}d`}
              color={t.textSecondary}
            />
          )}

          {/* Completed date */}
          {entry.status === 'complete' && entry.timing.lastSignoff && (
            <MetricChip
              label="Completed"
              value={formatDate(entry.timing.lastSignoff)}
              color={t.success}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const MetricChip: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => {
  const { t } = useTheme();
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs" style={{ color: t.textSecondary }}>{label}:</span>
      <span className="text-xs font-semibold" style={{ color }}>{value}</span>
    </div>
  );
};

function getStatusColor(status: TimelineStatus, t: any): string {
  switch (status) {
    case 'complete': return t.success;
    case 'on-track': return t.success;
    case 'at-risk': return '#f59e0b'; // amber
    case 'behind': return t.fail;
    case 'overdue': return t.fail;
    case 'no-deadline': return t.textSecondary;
  }
}
